import { createAsyncThunk } from '@reduxjs/toolkit';

import store from 'store/store';

import instance from 'utils/API';
import { VaultService } from 'utils/storage';

import { SubscriptionLevels } from 'core/enums/common';
import { infoNotify, successNotify } from 'core/helpers/toasts';
import { ActiveAccountDataType, SignInType, VerifyDataType } from 'core/types/base';

import { IUserReducer } from './types';

const vaultService = new VaultService();

export const addNewUser = createAsyncThunk(
	'user/registerUser',
	async (userData: IUserReducer, { rejectWithValue }) => {
		try {
			const response = await instance.post(`/auth/register`, userData);

			if (response.status === 200) return { errors: false, data: response.data };

			return { errors: true, data: response.data };
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const updateUserProfile = createAsyncThunk(
	'user/updateProfile',
	async (userProfileData: any, { rejectWithValue }) => {
		try {
			const { data } = await instance.post('profile/updateUserProfile', userProfileData.data, {
				params: {
					userId: userProfileData.id,
				},
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const updateUserIcon = createAsyncThunk(
	'user/updateUserAvatar',
	async (
		userAvatar: {
			id: string;
			img: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post('profile/uploadAvatar', {
				userId: userAvatar.id,
				base64Avatar: userAvatar.img,
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const deleteUserAccount = createAsyncThunk(
	'user/deleteUserAccount',
	async (
		userId: {
			id: string;
			function: () => void;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await instance.delete('profile/deleteUser', {
				params: {
					userId: userId.id,
				},
			});

			userId.function();
			return response?.data;
		} catch (error: any) {
			return rejectWithValue(error?.data);
		}
	}
);

export const changeUserPassword = createAsyncThunk(
	'user/changeUserPassword',
	async (
		userPassword: {
			id: string;
			data: {
				oldPassword: string;
				newPassword: string;
			};
			function: () => void;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post('profile/changeUserPassword', userPassword.data, {
				params: {
					userId: userPassword.id,
				},
			});
			userPassword.function();
			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const loginUser = createAsyncThunk<any, SignInType>(
	'user/loginUser',
	async (userData: SignInType, { rejectWithValue }) => {
		try {
			const response = await instance.post(`/auth/login`, userData.userData);

			if (response.status === 200) {
				const { jwt, ...userInfo } = response.data;

				vaultService.setItem('token', jwt);
				vaultService.setItem('user', userInfo);

				window.dispatchEvent(new Event('storage'));
			}

			return response;
		} catch (error: any) {
			return rejectWithValue(error);
		}
	}
);

export const forgotPasswordRequest = createAsyncThunk(
	'user/forgotPassword',
	async (data: any, { rejectWithValue }) => {
		try {
			return await instance.post(`/auth/forgotPassword`, data);
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const resetPassword = createAsyncThunk(
	'user/resetPassword',
	async (data: { id: string; token: string; password: string }, { rejectWithValue }) => {
		try {
			await instance.post(`/auth/resetPassword`, data);
		} catch (error: any) {
			return rejectWithValue(error.data);
		}

		return;
	}
);

export const sendReport = createAsyncThunk(
	'user/sendReport',
	async (
		data: {
			userEmail: string;
			userText: string;
			option: string;
			category: string;
			base64JpgFiles: string[];
		},
		{ rejectWithValue }
	) => {
		try {
			const reportData = {
				receiverEmail: process.env?.REACT_APP_FEEDBACK_FORM_EMAIL || '',
				userEmail: data.userEmail,
				userText: data.userText,
				option: data.option,
				category: data.category,
				base64JpgFiles: data.base64JpgFiles,
			};
			await instance.post(`/Report/sendReport`, reportData);
		} catch (error: any) {
			return rejectWithValue(error.data);
		}

		return;
	}
);

export const fetchUserBalance = createAsyncThunk(
	'user/fetchUserBalance',
	async (_, { rejectWithValue }) => {
		try {
			const currentSubscription = store?.getState().userRedux.currentSubscription;
			const activeBalance = store?.getState().userRedux.userBalance;
			const currentUserPlan = JSON.parse(localStorage.getItem('user') || '{}')?.accountPlan || 0;
			const hasPaidRequests =
				!activeBalance ||
				currentUserPlan === SubscriptionLevels.Basic ||
				currentSubscription === null;

			const isPaymentEnabled = process.env?.REACT_APP_ENABLE_PAYMENTS
				? process.env.REACT_APP_ENABLE_PAYMENTS.toLowerCase() === 'true'
				: true;

			if (isPaymentEnabled && hasPaidRequests) {
				const response: any = await instance.get(`/CheckOut/getUserBalance`);

				return response.data;
			}

			return activeBalance || 0;
		} catch (error: any) {
			return rejectWithValue(error?.data);
		}
	}
);

export const sentVerificationEmail = createAsyncThunk(
	'user/sentVerificationEmail',
	async (
		{
			redirectLink,
			userEmail,
			toastifyTitle,
			toastifyDescription,
			successFunction,
			errorFunction,
		}: VerifyDataType,
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/auth/sentVerificationEmail`, {
				redirectLink: redirectLink,
				userEmail: userEmail,
			});
			if (toastifyTitle && toastifyDescription) {
				infoNotify(toastifyTitle, toastifyDescription);
			}
			successFunction?.();
			return data;
		} catch (error: any) {
			errorFunction();
			return rejectWithValue(error.data);
		}
	}
);

export const activeUserAccount = createAsyncThunk(
	'user/activeAccount',
	async (
		{
			token,
			userEmail,
			toastifyTitle,
			toastifyDescription,
			successFunction,
			errorFunction,
		}: ActiveAccountDataType,
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/auth/activateUserAccount`, {
				token: token,
				userEmail: userEmail,
			});
			successNotify(toastifyTitle, toastifyDescription);
			successFunction();
			return data;
		} catch (error: any) {
			errorFunction();
			return rejectWithValue(error.data);
		}
	}
);

export const uploadDocumentUrl = createAsyncThunk(
	'user/uploadDocumentUrl',
	async (
		{
			fileName,
		}: {
			fileName: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.get(`/RAG/uploadDocumentUrl`, {
				params: {
					fileName,
				},
			});
			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getDocumentUrl = createAsyncThunk(
	'user/getDocumentUrl',
	async (
		{
			fileName,
			ownerId,
		}: {
			fileName: string;
			ownerId?: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.get(`/RAG/getDocumentUrl`, {
				params: {
					fileName,
					ownerId,
				},
			});
			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getUserDocuments = createAsyncThunk(
	'user/getUserDocuments',
	async (
		{ currentPageNumber, recordsPerPage }: { currentPageNumber: number; recordsPerPage: number },
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/RAG/getUserDocuments`, {
				currentPageNumber,
				recordsPerPage,
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const deleteUserDocument = createAsyncThunk(
	'user/deleteDocument',
	async (
		{
			fileName,
		}: {
			fileName: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.delete(`/RAG/deleteDocument`, {
				params: {
					fileName,
				},
			});
			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const verifyDocumentUpload = createAsyncThunk(
	'user/verifyDocumentUpload',
	async (
		{
			fileName,
		}: {
			fileName: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.get(`/RAG/verifyDocumentUpload`, {
				params: {
					fileName,
				},
			});
			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const embedDocument = createAsyncThunk(
	'user/embedDocument',
	async (
		{
			fileName,
		}: {
			fileName: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.get(`/RAG/embedDocument`, {
				params: {
					fileName,
				},
			});

			return { name: fileName, data };
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const findUserByEmail = createAsyncThunk(
	'user/findUserByEmail',
	async (
		{
			emailPrefix,
		}: {
			emailPrefix: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.get(`profile/findUserProfileByEmail`, {
				params: {
					emailPrefix,
				},
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getUserById = createAsyncThunk(
	'user/getUserById',
	async (
		{
			userId,
		}: {
			userId: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.get(`profile/getUserProfileById`, {
				params: {
					userId,
				},
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const givePermission = createAsyncThunk(
	'user/givePermission',
	async (
		fileData: {
			receiverId: string;
			documentName: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/RAG/givePermission`, fileData);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const removePermission = createAsyncThunk(
	'user/removePermission',
	async (
		fileData: {
			receiverId: string;
			documentName: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/RAG/removePermission`, fileData);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getAllUsersWithPermission = createAsyncThunk(
	'user/getAllUsersWithPermission',
	async (
		{
			currentPageNumber,
			recordsPerPage,
			fileName,
		}: { currentPageNumber: number; recordsPerPage: number; fileName: string },
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(
				`/RAG/getAllUsersWithPermission`,
				{
					currentPageNumber,
					recordsPerPage,
					fileName,
				},
				{
					params: {
						fileName,
					},
				}
			);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getUserProfile = createAsyncThunk(
	'user/getUserProfile',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/Profile/getUserProfile`);
			const { jwt, ...newUserData } = data;
			const user = JSON.parse(localStorage.getItem('user') || '');
			const token = vaultService.getItem('token');
			localStorage.setItem('user', JSON.stringify({ ...user, ...newUserData }));
			{
				token !== jwt && vaultService.setItem('token', jwt);
			}
			window.dispatchEvent(new Event('storage'));

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const createCustomer = createAsyncThunk(
	'user/createCustomer',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/Customer/createCustomer`);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const deleteCustomer = createAsyncThunk(
	'user/deleteCustomer',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await instance.delete(`/Customer/deleteCustomer`);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const updateCustomerData = createAsyncThunk(
	'user/updateCustomerData',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/Customer/updateCustomerData`);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const addCustomerPaymentMethod = createAsyncThunk(
	'user/addCustomerPaymentMethod',
	async (
		{
			connectionId,
			signalMethodName,
			successUrl,
			cancelUrl,
		}: {
			connectionId: string;
			signalMethodName: string;
			successUrl: string;
			cancelUrl: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/Customer/addCustomerPaymentMethod`, {
				connectionId,
				signalMethodName,
				successUrl,
				cancelUrl,
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const setDefaultPaymentMethod = createAsyncThunk(
	'user/setDefaultPaymentMethod',
	async (
		{
			paymentMethodId,
		}: {
			paymentMethodId: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.get(`/Customer/setDefaultPaymentMethod`, {
				params: {
					paymentMethodId,
				},
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getActiveSubscription = createAsyncThunk(
	'user/getActiveSubscription',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/Customer/getActiveSubscription`);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const createFreeSubscription = createAsyncThunk(
	'user/createFreeSubscription',
	async (
		{
			connectionId,
			signalMethodName,
		}: {
			connectionId?: string;
			signalMethodName?: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/Checkout/createFreeSubscription`, {
				connectionId,
				signalMethodName,
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const cancelSubscription = createAsyncThunk(
	'user/cancelSubscription',
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/CheckOut/cancelSubscription`);

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getAllPaymentMethods = createAsyncThunk(
	'user/getAllPaymentMethods',
	async (
		{
			recordsPerPage,
			startAfter,
			endBefore,
		}: {
			recordsPerPage: number;
			startAfter?: string;
			endBefore?: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/Customer/getAllPaymentMethods`, {
				recordsPerPage,
				startAfter,
				endBefore,
			});

			return { data, isEnd: !!endBefore, isStart: !!startAfter };
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const detachPaymentMethod = createAsyncThunk(
	'user/detachPaymentMethod',
	async ({ paymentMethodId }: { paymentMethodId: string }, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/Customer/detachPaymentMethod`, {
				params: {
					paymentMethodId,
				},
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getCustomerPayments = createAsyncThunk(
	'user/getCustomerPayments',
	async (
		{
			recordsPerPage,
			startAfter,
			endBefore,
		}: {
			recordsPerPage: number;
			startAfter?: string;
			endBefore?: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/Customer/getCustomerPayments`, {
				recordsPerPage,
				startAfter,
				endBefore,
			});

			return { data, isEnd: !!endBefore, isStart: !!startAfter };
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getProducts = createAsyncThunk('user/getProducts', async (_, { rejectWithValue }) => {
	try {
		const { data } = await instance.get(`/Product/getProducts`);

		return data;
	} catch (error: any) {
		return rejectWithValue(error.data);
	}
});

export const updateSubscriptionPreview = createAsyncThunk(
	'user/updateSubscriptionPreview',
	async ({ newPriceId }: { newPriceId: string }, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/Customer/updateSubscriptionPreview`, {
				params: {
					newPriceId,
				},
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const upgradeSubscription = createAsyncThunk(
	'user/upgradeSubscription',
	async ({ newPriceId }: { newPriceId: string }, { rejectWithValue }) => {
		try {
			const { data } = await instance.get(`/Checkout/upgradeSubscription`, {
				params: {
					newPriceId,
				},
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const changeUserEmail = createAsyncThunk(
	'user/changeUserEmail',
	async (
		{ redirectLink, userEmail }: { redirectLink: string; userEmail: string },
		{ rejectWithValue }
	) => {
		try {
			const { data } = await instance.post(`/Profile/changeUserEmail`, {
				redirectLink,
				userEmail,
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const verifyUserEmail = createAsyncThunk(
	'user/verifyUserEmail',
	async ({ token, userEmail }: { token: string; userEmail: string }, { rejectWithValue }) => {
		try {
			const { data } = await instance.post(`/Profile/verifyUserEmail`, {
				token,
				userEmail,
			});

			return data;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);
